"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const inquiryOptions = ["Ceza Hukuku", "Aile ve Boşanma Hukuku", "Kurumsal Hukuki Danışmanlık", "Şirketler Hukuku", "Gayrimenkul ve İnşaat Sözleşmeleri", "İşçi-İşveren Uyuşmazlıkları", "Sigorta ve Trafik Kazası Tazminatları", "İdari Yargı", "İcra Hukuku", "AYM/AİHM Bireysel Başvurular"];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-card/10 border border-border rounded-lg animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-6">
          <ArrowRight className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-slate-300 mb-3">Teşekkür Ederiz</h3>
        <p className="font-sans text-muted-foreground mb-8 max-w-md">
          {successMessage || "Başvurunuz başarıyla alınmıştır. En kısa sürede sizinle iletişime geçeceğiz."}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px]">
          Yeni Form Gönder
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Lütfen adınızı ve soyadınızı giriniz."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Adınız Soyadınız</FormLabel>
              <FormControl>
                <Input placeholder="Ahmet Yılmaz" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Telefon Numarası</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+90 5xx xxx xx xx" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "E-posta adresi zorunludur.",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Geçerli bir e-posta adresi giriniz."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">E-posta Adresi</FormLabel>
            <FormControl>
              <Input type="email" placeholder="ahmet@example.com" {...field} className="bg-background/50 border-input/50 focus:border-primary/50 transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Lütfen bir konu seçiniz."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Danışmanlık Konusu</FormLabel>
            <FormControl>
              <div className="relative">
                <select {...field} className="flex h-10 w-full items-center justify-between rounded-md border border-input/50 bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none text-foreground">
                  <option value="" disabled selected>Lütfen konuyu seçiniz</option>
                  {inquiryOptions.map((option, i) => <option key={i} value={option} className="bg-card text-foreground">
                      {option}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Lütfen mesajınızı giriniz."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Mesajınız</FormLabel>
            <FormControl>
              <Textarea placeholder="Konu hakkında detaylı bilgi veriniz..." className="min-h-[120px] bg-background/50 border-input/50 focus:border-primary/50 transition-colors resize-none" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/70">
              Sorununuzu veya danışmak istediğiniz konuyu ayrıntılı olarak açıklayınız.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Gönderiliyor...
          </> : "Gönder"}
      </Button>
      
      <p className="text-xs font-sans text-center text-muted-foreground mt-4">
        Gönderilen bilgiler 1136 sayılı Avukatlık Kanunu kapsamında gizli tutulacaktır.
      </p>
    </Form>;
}
export default function LegalConsultationContact() {
  return <section id="hukuki-danışmanlık-iletişim" className="dark bg-linear-to-br from-[#0a0c14] to-[#1a1c24] text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/20 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/20 to-transparent" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          
          {}
          <div className="space-y-10 pt-4">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight text-slate-300">
                İLETİŞİM
              </h2>
              <p className="text-lg font-sans text-muted-foreground font-light leading-relaxed max-w-md">
                Hukuki süreçlerinizde profesyonel destek almak ve randevu oluşturmak için bize ulaşın.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 size-12 rounded-sm bg-[#c5a059]/10 flex items-center justify-center border border-[#c5a059]/30 group-hover:border-[#c5a059]/60 transition-colors">
                  <MapPin className="size-5 text-[#c5a059]" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-serif font-medium text-slate-300 uppercase tracking-wider">Adres</h3>
                  <p className="text-base font-serif font-medium text-slate-200">Avukat & Arabulucu Muhammed Çelik</p>
                  <p className="text-lg font-sans text-foreground leading-snug">
                    Kutlubey Mah. Cengiz Topel Cad. 1006 Sk. No:31/1 Merkez/ISPARTA
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 size-12 rounded-sm bg-[#c5a059]/10 flex items-center justify-center border border-[#c5a059]/30 group-hover:border-[#c5a059]/60 transition-colors">
                  <Phone className="size-5 text-[#c5a059]" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-serif font-medium text-slate-300 uppercase tracking-wider">Telefon</h3>
                  <p className="text-lg font-sans text-foreground">
                    <Link to="tel:+905548421537" className="hover:text-primary transition-colors">
                      +90 554 842 15 37
                    </Link>
                  </p>
                  <p className="text-sm font-sans text-muted-foreground">Hafta içi 09:00 - 18:00</p>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 size-12 rounded-sm bg-[#c5a059]/10 flex items-center justify-center border border-[#c5a059]/30 group-hover:border-[#c5a059]/60 transition-colors">
                  <Mail className="size-5 text-[#c5a059]" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-serif font-medium text-slate-300 uppercase tracking-wider">E-posta</h3>
                  <p className="text-lg font-sans text-foreground">
                    <Link to="mailto:av.celik@mail.com" className="hover:text-primary transition-colors">
                      av.celik@mail.com
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border/30">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Clock className="size-4" />
                <span className="text-sm font-sans">Pazartesi - Cuma: 09:00 - 18:00</span>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card/5 backdrop-blur-sm border border-border/40 p-6 md:p-8 lg:p-10 rounded-sm shadow-2xl shadow-black/20">
            <div className="mb-8">
              <h3 className="text-2xl font-serif text-slate-300 mb-2">Randevu ve Bilgi Talebi</h3>
              <p className="font-sans text-muted-foreground text-sm">
                Aşağıdaki formu doldurarak hukuki danışmanlık talebinizi bize iletebilirsiniz.
              </p>
            </div>

            <FormProvider formId="legal_consultation_contact_home" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Gönder" successMessage="Talebiniz alınmıştır. En kısa sürede dönüş yapılacaktır.">
              <ContactForm />
            </FormProvider>
          </div>

        </div>

        {}
        <div className="mt-16 rounded-lg border-2 border-border/40 overflow-hidden shadow-xl shadow-black/20 bg-card/5">
          <iframe src="https://maps.google.com/maps?q=Kutlubey%20Mah.%20Cengiz%20Topel%20Cad.%201006%20Sk.%20No%3A31%2F1%20Merkez%2FISPARTA&t=&z=16&ie=UTF8&iwloc=&output=embed" width="100%" className="w-full h-[350px] md:h-[400px] lg:h-[450px] border-0" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" aria-label="Business location map" />
        </div>

      </div>
    </section>;
}